/**
 * Role-based permissions configuration for Fieldz AI
 * 
 * Roles:
 * - owner: Full access to everything (business owner)
 * - admin: Full access to everything except billing changes
 * - dispatcher: Limited access - can manage jobs, customers, messages but NOT settings/team/billing
 */

// Pages that dispatchers are NOT allowed to access
const DISPATCHER_RESTRICTED_PAGES = [
  '/dashboard/team',
  '/dashboard/financials',
  '/dashboard/expenses',
  '/dashboard/analytics',
  '/dashboard/automations',
  '/dashboard/campaigns',
];

// Pages that only owners can access
const OWNER_ONLY_PAGES = [
  // Currently none - admins have almost full access
];

/**
 * Check if a user can access a specific page
 * @param {Object} user - User object with role property
 * @param {string} path - The page path to check
 * @returns {boolean} - Whether the user can access the page
 */
export const canAccessPage = (user, path) => {
  if (!user) return false;
  
  const role = user.role || 'owner'; // Default to owner for backward compatibility
  
  // Technicians must NEVER access dashboard routes
  if (role === 'technician') {
    return false;
  }
  
  // Owners and admins can access everything
  if (role === 'owner' || role === 'admin') {
    return true;
  }
  
  // Dispatchers have restricted access
  if (role === 'dispatcher') {
    // Check if path starts with any restricted page
    return !DISPATCHER_RESTRICTED_PAGES.some(restricted => 
      path === restricted || path.startsWith(restricted + '/')
    );
  }
  
  // Default: allow access for unknown roles (fail open for now)
  return true;
};

/**
 * Filter navigation items based on user role
 * @param {Object} user - User object with role property
 * @param {Array} navItems - Array of navigation items with path property
 * @returns {Array} - Filtered navigation items
 */
export const filterNavItems = (user, navItems) => {
  if (!user) return [];
  
  return navItems.filter(item => canAccessPage(user, item.path));
};

/**
 * Get the user's role display name
 * @param {Object} user - User object
 * @returns {string} - Human-readable role name
 */
export const getRoleDisplayName = (user) => {
  if (!user) return 'User';
  
  const role = user.role || 'owner';
  
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'admin':
      return 'Admin';
    case 'dispatcher':
      return 'Dispatcher';
    case 'technician':
      return 'Technician';
    default:
      return 'User';
  }
};

/**
 * Check if user is a technician
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isTechnician = (user) => {
  return user?.role === 'technician';
};

/**
 * Check if user is a dispatcher (has limited permissions)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isDispatcher = (user) => {
  return user?.role === 'dispatcher';
};

/**
 * Check if user is an owner or admin (has full permissions)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasFullAccess = (user) => {
  const role = user?.role || 'owner';
  return role === 'owner' || role === 'admin';
};

export default {
  canAccessPage,
  filterNavItems,
  getRoleDisplayName,
  isTechnician,
  isDispatcher,
  hasFullAccess,
  DISPATCHER_RESTRICTED_PAGES,
};
